/**
 * Selezione multipla a chip/checkbox per liste codificate (es. HP).
 * Campo con ricerca: niente stringhe libere "HP4, HP14"; l'utente seleziona
 * checkbox dalle codifiche ufficiali.
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import type { RentriChoice } from "@/lib/rentriChoices";

interface ChoiceMultiSelectProps {
  id?: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: RentriChoice[];
  loading?: boolean;
  emptyText?: string;
  className?: string;
  /** Filtra inline le opzioni (utile se la lista è lunga). */
  searchable?: boolean;
}

export function ChoiceMultiSelect({
  id,
  values,
  onChange,
  options,
  loading,
  emptyText = "Nessuna opzione disponibile",
  className,
  searchable = true,
}: ChoiceMultiSelectProps) {
  const [query, setQuery] = React.useState("");
  const set = React.useMemo(() => new Set(values), [values]);

  const filtered = query.trim()
    ? options.filter((o) => {
        const q = query.trim().toLowerCase();
        return (
          o.value.toLowerCase().includes(q) ||
          o.label.toLowerCase().includes(q) ||
          (o.description ?? "").toLowerCase().includes(q)
        );
      })
    : options;

  const toggle = (v: string) => {
    if (set.has(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };

  return (
    <div id={id} className={cn("space-y-2", className)}>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((v) => {
            const opt = options.find((o) => o.value === v);
            return (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-0.5 text-xs"
                title={opt?.description ?? undefined}
              >
                <span className="font-mono">{opt?.label ?? v}</span>
                <button
                  type="button"
                  onClick={() => toggle(v)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`Rimuovi ${v}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      {searchable && options.length > 6 && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filtra…"
          className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      )}
      <div className="max-h-44 overflow-y-auto rounded-md border bg-background">
        {loading && (
          <p className="px-3 py-2 text-xs text-muted-foreground">Caricamento…</p>
        )}
        {!loading && filtered.length === 0 && (
          <p className="px-3 py-2 text-xs text-muted-foreground">{emptyText}</p>
        )}
        {filtered.map((opt) => (
          <label
            key={opt.value}
            className="flex cursor-pointer items-start gap-2 px-3 py-1.5 text-xs hover:bg-accent"
          >
            <input
              type="checkbox"
              checked={set.has(opt.value)}
              onChange={() => toggle(opt.value)}
              className="mt-0.5"
            />
            <span className="font-mono">{opt.label}</span>
            {opt.description && (
              <span className="text-muted-foreground">— {opt.description}</span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}
